import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Accordion, Badge } from 'react-bootstrap';
import { FaGraduationCap, FaBook, FaUniversity, FaChalkboardTeacher, 
         FaCalendarAlt, FaMedal, FaUserGraduate, FaCheckCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/iitjam/maths.css';

const MathematicsPage = () => {
  // Calculate next two years for program preparation
  const [years, setYears] = useState({
    firstYear: 0,
    secondYear: 0
  });

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setYears({
      firstYear: currentYear + 1,
      secondYear: currentYear + 2
    });
  }, []);

  // Animation variants
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  // Syllabus data
  const syllabus = [
    {
      title: "Sequences and Series",
      topics: [
        "Convergence of sequences of real numbers",
        "Cauchy sequences",
        "Monotone sequences",
        "Bolzano-Weierstrass theorem",
        "Convergence of series of real numbers",
        "Tests of convergence (comparison, ratio, root)",
        "Alternating series",
        "Conditional and absolute convergence"
      ]
    },
    {
      title: "Differential Calculus",
      topics: [
        "Functions of one variable",
        "Limit, continuity and differentiability",
        "Mean value theorems",
        "Taylor's theorem",
        "Maxima and minima",
        "Functions of two variables",
        "Limit, continuity and partial derivatives",
        "Homogeneous functions",
        "Euler's theorem"
      ]
    },
    {
      title: "Integral Calculus",
      topics: [
        "Riemann integration",
        "Fundamental theorem of calculus",
        "Improper integrals",
        "Double and triple integrals",
        "Change of order of integration",
        "Applications: area, volume, surface area"
      ]
    },
    {
      title: "Matrices",
      topics: [
        "Algebra of matrices",
        "Rank of a matrix",
        "Determinants",
        "System of linear equations",
        "Eigenvalues and eigenvectors",
        "Cayley-Hamilton theorem",
        "Symmetric, skew-symmetric, hermitian matrices",
        "Diagonalization of matrices"
      ]
    },
    {
      title: "Vector Calculus",
      topics: [
        "Vector functions of one variable",
        "Differentiation and integration",
        "Scalar and vector fields",
        "Gradient, divergence, curl",
        "Line integrals, surface integrals",
        "Green's, Stokes', and Gauss' theorems"
      ]
    },
    {
      title: "Complex Analysis",
      topics: [
        "Complex numbers and functions",
        "Analyticity and Cauchy-Riemann equations",
        "Cauchy's integral theorem and formula",
        "Power series and Laurent series",
        "Residue theorem and applications",
        "Conformal mappings"
      ]
    },
    {
      title: "Ordinary Differential Equations",
      topics: [
        "First order ODEs",
        "Linear ODEs of higher order with constant coefficients",
        "Methods of variation of parameters and undetermined coefficients",
        "Series solutions for ODEs",
        "Systems of linear ODEs"
      ]
    },
    {
      title: "Abstract Algebra",
      topics: [
        "Groups, subgroups, normal subgroups",
        "Lagrange's theorem",
        "Group homomorphisms and isomorphisms",
        "Rings, ideals, integral domains, fields",
        "Ring homomorphisms",
        "Field extensions, finite fields"
      ]
    }
  ];

  // Training modules
  const trainingModules = [
    {
      title: "Foundation Module",
      duration: "3 months",
      description: "Covers fundamental concepts of Mathematics with focus on building strong basics",
      features: [
        "Comprehensive study material",
        "Weekly tests and assessments",
        "Doubt-clearing sessions",
        "Previous years' question papers"
      ]
    },
    {
      title: "Advanced Concepts Module",
      duration: "4 months",
      description: "Deep dive into advanced topics with problem-solving approaches",
      features: [
        "Advanced problem solving techniques",
        "Topic-wise mock tests",
        "One-on-one mentoring sessions",
        "Special classes on difficult topics"
      ]
    },
    {
      title: "Test Series and Revision",
      duration: "2 months",
      description: "Intensive practice and revision to ensure exam readiness",
      features: [
        "Full-length mock tests",
        "Comprehensive revision sessions",
        "Performance analysis and feedback",
        "Exam strategy sessions"
      ]
    },
    {
      title: "Crash Course",
      duration: "1 month",
      description: "Last-minute preparation and quick revision of all important topics",
      features: [
        "Quick revision of key concepts",
        "Important questions and solutions",
        "Time management techniques",
        "Exam day tips and strategies"
      ]
    }
  ];

  // IITs and NITs offering admission through JAM
  const institutes = [
    {
      name: "Indian Institute of Technology (IIT) Bombay",
      programs: ["M.Sc. Mathematics", "M.Sc.-Ph.D. Dual Degree in Mathematics"]
    },
    {
      name: "Indian Institute of Technology (IIT) Delhi",
      programs: ["M.Sc. Mathematics", "M.Tech. Mathematics and Computing"]
    },
    {
      name: "Indian Institute of Technology (IIT) Madras",
      programs: ["M.Sc. Mathematics"]
    },
    {
      name: "Indian Institute of Technology (IIT) Kanpur",
      programs: ["M.Sc. Mathematics", "M.Sc.-Ph.D. Dual Degree in Mathematics"]
    },
    {
      name: "Indian Institute of Technology (IIT) Kharagpur",
      programs: ["M.Sc. Mathematics and Computing"]
    },
    {
      name: "Indian Institute of Technology (IIT) Roorkee",
      programs: ["M.Sc. Mathematics"]
    },
    {
      name: "Indian Institute of Technology (IIT) Guwahati",
      programs: ["M.Sc. Mathematics and Computing"]
    },
    {
      name: "National Institute of Technology (NIT) Tiruchirappalli",
      programs: ["M.Sc. Mathematics"]
    },
    {
      name: "National Institute of Technology (NIT) Surathkal",
      programs: ["M.Sc. Mathematics"]
    },
    {
      name: "National Institute of Technology (NIT) Warangal",
      programs: ["M.Sc. Mathematics and Scientific Computing"]
    }
  ];

  // Instructors
  const instructors = [
    {
      name: "Dr. Anand Sharma",
      qualification: "Ph.D. Mathematics, IIT Delhi",
      experience: "15+ years of teaching experience",
      specialization: "Abstract Algebra, Real Analysis"
    },
    {
      name: "Prof. Meera Gupta",
      qualification: "Ph.D. Mathematics, IISc Bangalore",
      experience: "12+ years of teaching experience",
      specialization: "Complex Analysis, Differential Equations"
    },
    {
      name: "Dr. Rajiv Verma",
      qualification: "M.Sc. Mathematics, IIT Bombay",
      experience: "10+ years of teaching experience",
      specialization: "Linear Algebra, Vector Calculus"
    }
  ];

  return (
    <div className="mathematics-page">
      {/* Hero Section */}
      <div className="hero-section text-white">
        <Container>
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeIn}
          >
            <h1 className="display-4">IIT-JAM {years.firstYear} Mathematics</h1>
            <h3>Preparing Future Mathematicians for {years.firstYear} & {years.secondYear}</h3>
            <p className="lead">
              Master the concepts with our comprehensive training program led by IIT alumni and subject experts
            </p>
            <Button variant="light" size="lg" className="mt-3 me-3">Enroll Now</Button>
            <Button variant="outline-light" size="lg" className="mt-3">Download Brochure</Button>
          </motion.div>
        </Container>
      </div>

      {/* Overview Section */}
      <section className="overview-section py-5">
        <Container>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="section-title text-center mb-5">
              <FaGraduationCap className="me-2" />
              Course Overview
            </h2>
            <Row>
              <Col md={6}>
                <div className="overview-content">
                  <h3>Why Choose Our IIT-JAM Mathematics Program?</h3>
                  <p>
                    Our comprehensive program is designed specifically for aspirants aiming to excel in the IIT-JAM Mathematics examination. With a systematic approach to the entire syllabus, rigorous practice sessions, and personalized guidance, we ensure that you are thoroughly prepared for the challenging exam.
                  </p>
                  <ul className="check-list">
                    <li><FaCheckCircle className="text-success me-2" /> Comprehensive coverage of entire syllabus</li>
                    <li><FaCheckCircle className="text-success me-2" /> Regular mock tests and assessments</li>
                    <li><FaCheckCircle className="text-success me-2" /> Live doubt-clearing sessions</li>
                    <li><FaCheckCircle className="text-success me-2" /> Study materials prepared by IIT alumni</li>
                    <li><FaCheckCircle className="text-success me-2" /> Previous years' papers with detailed solutions</li>
                  </ul>
                </div>
              </Col>
              <Col md={6}>
                <div className="overview-stats">
                  <div className="stats-card">
                    <div className="stats-icon">
                      <FaMedal className="text-warning" />
                    </div>
                    <div className="stats-info">
                      <h4>95%</h4>
                      <p>Success Rate</p>
                    </div>
                  </div>
                  <div className="stats-card">
                    <div className="stats-icon">
                      <FaUserGraduate className="text-info" />
                    </div>
                    <div className="stats-info">
                      <h4>5000+</h4>
                      <p>Students Trained</p>
                    </div>
                  </div>
                  <div className="stats-card">
                    <div className="stats-icon">
                      <FaUniversity className="text-danger" />
                    </div>
                    <div className="stats-info">
                      <h4>100+</h4>
                      <p>Top IIT Selections</p>
                    </div>
                  </div>
                  <div className="stats-card">
                    <div className="stats-icon">
                      <FaChalkboardTeacher className="text-success" />
                    </div>
                    <div className="stats-info">
                      <h4>20+</h4>
                      <p>Expert Faculty</p>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Syllabus Section */}
      <section className="syllabus-section py-5 bg-light">
        <Container>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="section-title text-center mb-5">
              <FaBook className="me-2" />
              IIT-JAM Mathematics Syllabus
            </h2>
            <Accordion defaultActiveKey="0" className="syllabus-accordion">
              {syllabus.map((unit, index) => (
                <Accordion.Item eventKey={index.toString()} key={index}>
                  <Accordion.Header>
                    <span className="fw-bold">{unit.title}</span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <ul className="topic-list">
                      {unit.topics.map((topic, i) => (
                        <li key={i}>{topic}</li>
                      ))}
                    </ul>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </motion.div>
        </Container>
      </section>

      {/* Training Modules Section */}
      <section className="modules-section py-5">
        <Container>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="section-title text-center mb-5">
              <FaCalendarAlt className="me-2" />
              Training Modules
            </h2>
            <Row>
              {trainingModules.map((module, index) => (
                <Col md={6} lg={3} key={index} className="mb-4">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card className="module-card h-100">
                      <Card.Body>
                        <div className="module-icon">
                          {index === 0 && <FaBook className="text-primary" />}
                          {index === 1 && <FaGraduationCap className="text-success" />}
                          {index === 2 && <FaUniversity className="text-danger" />}
                          {index === 3 && <FaMedal className="text-warning" />}
                        </div>
                        <Card.Title>{module.title}</Card.Title>
                        <Badge bg="info" className="mb-2">{module.duration}</Badge>
                        <Card.Text>{module.description}</Card.Text>
                        <ul className="module-features">
                          {module.features.map((feature, i) => (
                            <li key={i}><FaCheckCircle className="text-success me-2" /> {feature}</li>
                          ))}
                        </ul>
                      </Card.Body>
                      <Card.Footer>
                        <Button variant="outline-primary" className="w-100">Learn More</Button>
                      </Card.Footer>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Institutes Section */}
      <section className="institutes-section py-5 bg-light">
        <Container>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="section-title text-center mb-5">
              <FaUniversity className="me-2" />
              Institutes Accepting IIT-JAM Score
            </h2>
            <Row>
              {institutes.map((institute, index) => (
                <Col md={6} lg={4} key={index} className="mb-4">
                  <Card className="institute-card h-100">
                    <Card.Body>
                      <Card.Title>{institute.name}</Card.Title>
                      <Card.Subtitle className="mb-3 text-muted">Programs Offered:</Card.Subtitle>
                      <ul className="program-list">
                        {institute.programs.map((program, i) => (
                          <li key={i}>{program}</li>
                        ))}
                      </ul>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Faculty Section */}
      <section className="faculty-section py-5">
        <Container>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="section-title text-center mb-5">
              <FaChalkboardTeacher className="me-2" />
              Our Expert Faculty
            </h2>
            <Row>
              {instructors.map((instructor, index) => (
                <Col md={4} key={index} className="mb-4">
                  <motion.div
                    whileHover={{ y: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card className="faculty-card h-100">
                      <div className="faculty-img-container">
                        <div className="faculty-img-placeholder">
                          {instructor.name.split(' ').map(n => n[0]).join('')}
                        </div>
                      </div>
                      <Card.Body>
                        <Card.Title>{instructor.name}</Card.Title>
                        <Card.Subtitle className="mb-2 text-muted">{instructor.qualification}</Card.Subtitle>
                        <Card.Text>
                          <strong>Experience:</strong> {instructor.experience}<br />
                          <strong>Specialization:</strong> {instructor.specialization}
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section text-white text-center py-5">
        <Container>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="mb-4">Ready to Start Your IIT-JAM {years.firstYear} Preparation?</h2>
            <p className="lead mb-4">Join our comprehensive program and take the first step towards your dream institute</p>
            <Button variant="light" size="lg" className="me-3">Enroll Now</Button>
            <Button variant="outline-light" size="lg">Request a Demo Class</Button>
          </motion.div>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="faq-section py-5 bg-light">
        <Container>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="section-title text-center mb-5">Frequently Asked Questions</h2>
            <Accordion className="faq-accordion">
              <Accordion.Item eventKey="0">
                <Accordion.Header>What is the eligibility criteria for IIT-JAM Mathematics?</Accordion.Header>
                <Accordion.Body>
                  Candidates must have a Bachelor's degree with Mathematics as a subject for at least two years/four semesters. Final year students can also apply.
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="1">
                <Accordion.Header>How is the IIT-JAM Mathematics exam structured?</Accordion.Header>
                <Accordion.Body>
                  The exam consists of three sections: Section A with Multiple Choice Questions (MCQs), Section B with Multiple Select Questions (MSQs), and Section C with Numerical Answer Type (NAT) questions. The total marks for the exam is 100.
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="2">
                <Accordion.Header>What is the duration of your training program?</Accordion.Header>
                <Accordion.Body>
                  Our complete training program spans 10 months, divided into Foundation Module (3 months), Advanced Concepts Module (4 months), Test Series and Revision (2 months), and Crash Course (1 month). However, you can also opt for individual modules based on your requirements.
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="3">
                <Accordion.Header>Do you provide study materials?</Accordion.Header>
                <Accordion.Body>
                  Yes, we provide comprehensive study materials prepared by IIT alumni and subject experts. These include detailed notes for each topic, solved examples, practice problems, and previous years' question papers with solutions.
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="4">
                <Accordion.Header>Is there any scholarship available?</Accordion.Header>
                <Accordion.Body>
                  Yes, we offer merit-based scholarships to deserving candidates. Students can appear for our scholarship test and receive up to 50% waiver on the course fee based on their performance.
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </motion.div>
        </Container>
      </section>

     
    </div>
  );
};

export default MathematicsPage;